import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Buildings, Plus, Trash, UserCircle, CalendarBlank, CurrencyDollar,
  PencilSimple, GearSix, ChartLineUp, CaretDown, CaretUp, X
} from "@phosphor-icons/react";

const fmtM = (n) => n ? `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 0 })}` : "$0";
const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

export default function BudgetModule() {
  const { user, token } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "auxiliar";
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [budgetData, setBudgetData] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [productFields, setProductFields] = useState([]);
  const [kpiColumns, setKpiColumns] = useState([]);
  const [expandedAdvisor, setExpandedAdvisor] = useState(null);
  const [advisorMetas, setAdvisorMetas] = useState({});
  const [advisorKpis, setAdvisorKpis] = useState({});
  const [showNewBranch, setShowNewBranch] = useState(false);
  const [showNewAdvisor, setShowNewAdvisor] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [newBranch, setNewBranch] = useState("");
  const [newAdvisor, setNewAdvisor] = useState({ name: "", hire_date: "", meta_mensual: "", participacion_pct: "" });
  const [newField, setNewField] = useState({ name: "", type: "number" });
  const [newCol, setNewCol] = useState({ name: "", type: "number" });
  const [editCell, setEditCell] = useState(null);
  const [editVal, setEditVal] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const loadBranches = useCallback(async () => {
    try {
      const [bRes, pfRes, kcRes] = await Promise.all([
        axios.get(`${API}/budget/branches`, { headers }),
        axios.get(`${API}/budget/product-fields`, { headers }),
        axios.get(`${API}/budget/kpi-columns`, { headers }),
      ]);
      setBranches(bRes.data || []);
      setProductFields(pfRes.data || []);
      setKpiColumns(kcRes.data || []);
      if (!selectedBranch && bRes.data.length > 0) setSelectedBranch(bRes.data[0].id);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  const loadBranchData = useCallback(async () => {
    if (!selectedBranch) return;
    try {
      const [bRes, aRes] = await Promise.all([
        axios.get(`${API}/budget/table/${selectedBranch}?year=${year}`, { headers }),
        axios.get(`${API}/budget/advisors/${selectedBranch}`, { headers }),
      ]);
      setBudgetData(bRes.data || []);
      setAdvisors(aRes.data || []);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token, selectedBranch, year]);

  useEffect(() => { loadBranchData(); }, [loadBranchData]);

  const loadAdvisorDetail = async (advId) => {
    try {
      const [mRes, kRes] = await Promise.all([
        axios.get(`${API}/budget/advisor-metas/${advId}`, { headers }),
        axios.get(`${API}/budget/advisor-kpis/${advId}?year=${year}`, { headers }),
      ]);
      setAdvisorMetas(prev => ({ ...prev, [advId]: mRes.data || {} }));
      setAdvisorKpis(prev => ({ ...prev, [advId]: kRes.data || {} }));
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  };

  const handleToggleAdvisor = (advId) => {
    if (expandedAdvisor === advId) { setExpandedAdvisor(null); return; }
    setExpandedAdvisor(advId);
    loadAdvisorDetail(advId);
  };

  // Save handlers
  const saveBudgetCell = async (month, field) => {
    const v = parseFloat(editVal); if (isNaN(v)) { setEditCell(null); return; }
    try {
      await axios.post(`${API}/budget/table`, { branch_id: selectedBranch, year, month, field, value: v }, { headers });
      setEditCell(null); loadBranchData();
    } catch { toast.error("Error"); }
  };

  const saveAdvisorMeta = async (advId, fieldId) => {
    const v = parseFloat(editVal); if (isNaN(v)) { setEditCell(null); return; }
    try {
      await axios.post(`${API}/budget/advisor-metas`, { advisor_id: advId, field_id: fieldId, value: v }, { headers });
      setEditCell(null); loadAdvisorDetail(advId);
    } catch { toast.error("Error"); }
  };

  const saveAdvisorKpi = async (advId, month, colId) => {
    const v = parseFloat(editVal); if (isNaN(v)) { setEditCell(null); return; }
    try {
      await axios.post(`${API}/budget/advisor-kpis`, { advisor_id: advId, year, month, column_id: colId, value: v }, { headers });
      setEditCell(null); loadAdvisorDetail(advId);
    } catch { toast.error("Error"); }
  };

  const handleCreateBranch = async () => {
    if (!newBranch.trim()) return;
    await axios.post(`${API}/budget/branches`, { name: newBranch }, { headers });
    setNewBranch(""); setShowNewBranch(false); loadBranches();
    toast.success("Sucursal creada");
  };

  const handleCreateAdvisor = async () => {
    if (!newAdvisor.name.trim()) { toast.error("Nombre requerido"); return; }
    await axios.post(`${API}/budget/advisors`, { ...newAdvisor, branch_id: selectedBranch, meta_mensual: parseFloat(newAdvisor.meta_mensual) || 0, participacion_pct: parseFloat(newAdvisor.participacion_pct) || 0 }, { headers });
    setNewAdvisor({ name: "", hire_date: "", meta_mensual: "", participacion_pct: "" }); setShowNewAdvisor(false); loadBranchData();
    toast.success("Asesor agregado");
  };

  const handleDeleteAdvisor = async (id) => {
    if (!window.confirm("Eliminar este asesor?")) return;
    await axios.delete(`${API}/budget/advisors/${id}`, { headers }); loadBranchData();
  };

  const handleAddProductField = async () => {
    if (!newField.name.trim()) return;
    await axios.post(`${API}/budget/product-fields`, newField, { headers });
    setNewField({ name: "", type: "number" }); loadBranches();
    toast.success("Campo agregado");
  };

  const handleAddKpiColumn = async () => {
    if (!newCol.name.trim()) return;
    await axios.post(`${API}/budget/kpi-columns`, newCol, { headers });
    setNewCol({ name: "", type: "number" }); loadBranches();
    toast.success("Columna agregada");
  };

  const branchName = branches.find(b => b.id === selectedBranch)?.name || "";

  const EditableCell = ({ cellKey, value, onSave, fmt }) => {
    const isEditing = editCell === cellKey;
    if (!isAdmin) return <span className="text-xs">{fmt ? fmt(value) : value}</span>;
    if (isEditing) return (
      <input type="number" value={editVal} onChange={e => setEditVal(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") onSave(); if (e.key === "Escape") setEditCell(null); }}
        onBlur={onSave} className="w-20 h-6 text-[10px] border border-indigo-300 rounded px-1 text-center bg-indigo-50" autoFocus />
    );
    return (
      <button onClick={() => { setEditCell(cellKey); setEditVal(String(value || 0)); }}
        className="text-xs hover:bg-indigo-50 hover:text-indigo-600 px-1 py-0.5 rounded cursor-pointer">
        {fmt ? fmt(value) : value || "—"}
      </button>
    );
  };

  return (
    <div className="space-y-5" data-testid="budget-module">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Buildings size={28} weight="fill" className="text-teal-600" /> Presupuestos</h1>
        {isAdmin && <Button size="sm" variant="outline" onClick={() => setShowConfig(!showConfig)} data-testid="budget-config-btn"><GearSix size={14} className="mr-1" /> Configurar Campos</Button>}
      </div>

      {/* Config Panel */}
      {showConfig && isAdmin && (
        <div className="bg-slate-50 border rounded-xl p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Product Fields Config */}
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Meta por Producto (campos configurables)</p>
              <div className="space-y-1 mb-2">
                {productFields.map(f => (
                  <div key={f.id} className="flex items-center justify-between bg-white border rounded px-3 py-1.5 text-xs">
                    <span>{f.name} <span className="text-slate-400">({f.type})</span></span>
                    <button onClick={async () => { await axios.delete(`${API}/budget/product-fields/${f.id}`, { headers }); loadBranches(); }} className="text-red-400 hover:text-red-600"><Trash size={12} /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-1">
                <Input value={newField.name} onChange={e => setNewField({ ...newField, name: e.target.value })} placeholder="Nuevo campo..." className="h-8 text-xs flex-1" />
                <select value={newField.type} onChange={e => setNewField({ ...newField, type: e.target.value })} className="border rounded px-1 text-xs h-8">
                  <option value="number">Numero</option><option value="percentage">%</option><option value="money">$</option>
                </select>
                <Button size="sm" className="h-8" onClick={handleAddProductField}><Plus size={12} /></Button>
              </div>
            </div>
            {/* KPI Columns Config */}
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">KPIs Mensuales (columnas configurables)</p>
              <div className="space-y-1 mb-2">
                {kpiColumns.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-white border rounded px-3 py-1.5 text-xs">
                    <span>{c.name} <span className="text-slate-400">({c.type})</span></span>
                    <button onClick={async () => { await axios.delete(`${API}/budget/kpi-columns/${c.id}`, { headers }); loadBranches(); }} className="text-red-400 hover:text-red-600"><Trash size={12} /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-1">
                <Input value={newCol.name} onChange={e => setNewCol({ ...newCol, name: e.target.value })} placeholder="Nueva columna..." className="h-8 text-xs flex-1" />
                <select value={newCol.type} onChange={e => setNewCol({ ...newCol, type: e.target.value })} className="border rounded px-1 text-xs h-8">
                  <option value="number">Numero</option><option value="money">$</option><option value="percentage">%</option>
                </select>
                <Button size="sm" className="h-8" onClick={handleAddKpiColumn}><Plus size={12} /></Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Branch Selector + Year */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          {branches.map(b => (
            <button key={b.id} onClick={() => setSelectedBranch(b.id)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${selectedBranch === b.id ? "bg-teal-600 text-white" : "bg-white border text-slate-700 hover:bg-slate-50"}`}
              data-testid={`branch-${b.id}`}><Buildings size={14} /> {b.name}</button>
          ))}
          {isAdmin && (
            showNewBranch ? (
              <div className="flex gap-1">
                <Input value={newBranch} onChange={e => setNewBranch(e.target.value)} placeholder="Nombre sucursal" className="h-9 text-xs w-32" data-testid="new-branch-input"
                  onKeyDown={e => { if (e.key === "Enter") handleCreateBranch(); }} />
                <Button size="sm" onClick={handleCreateBranch}><Plus size={12} /></Button>
                <Button size="sm" variant="outline" onClick={() => setShowNewBranch(false)}><X size={12} /></Button>
              </div>
            ) : <button onClick={() => setShowNewBranch(true)} className="px-3 py-2 border border-dashed rounded-lg text-xs text-slate-400 hover:text-teal-600" data-testid="add-branch-btn"><Plus size={12} className="inline mr-1" /> Sucursal</button>
          )}
        </div>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="border rounded-lg px-2 py-1.5 text-sm ml-auto">
          {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {!selectedBranch ? <p className="text-center text-slate-400 py-12">Selecciona o crea una sucursal</p> : (
        <>
          {/* Budget Table */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="bg-teal-600 px-5 py-3">
              <h3 className="text-white font-bold flex items-center gap-2"><CurrencyDollar size={18} /> Presupuesto {branchName} — {year}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="budget-table">
                <thead><tr className="bg-slate-50 border-b">
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500">MES</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-slate-500">PRESUPUESTO</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-slate-500">REAL</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-slate-500">META COMERCIAL</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-slate-500">REAL COMERCIAL</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-slate-500">% AVANCE</th>
                </tr></thead>
                <tbody className="divide-y">
                  {budgetData.map(row => {
                    const pct = row.presupuesto > 0 ? (row.real / row.presupuesto) : 0;
                    return (
                      <tr key={row.month} className="hover:bg-slate-50" data-testid={`budget-row-${row.month}`}>
                        <td className="px-3 py-2 font-medium text-slate-700">{MONTHS[row.month - 1]}</td>
                        <td className="px-3 py-2 text-right"><EditableCell cellKey={`b-${row.month}-presupuesto`} value={row.presupuesto} onSave={() => saveBudgetCell(row.month, "presupuesto")} fmt={fmtM} /></td>
                        <td className="px-3 py-2 text-right font-semibold"><EditableCell cellKey={`b-${row.month}-real`} value={row.real} onSave={() => saveBudgetCell(row.month, "real")} fmt={fmtM} /></td>
                        <td className="px-3 py-2 text-right"><EditableCell cellKey={`b-${row.month}-meta_comercial`} value={row.meta_comercial} onSave={() => saveBudgetCell(row.month, "meta_comercial")} fmt={fmtM} /></td>
                        <td className="px-3 py-2 text-right font-semibold"><EditableCell cellKey={`b-${row.month}-real_comercial`} value={row.real_comercial} onSave={() => saveBudgetCell(row.month, "real_comercial")} fmt={fmtM} /></td>
                        <td className="px-3 py-2 text-center">
                          {row.presupuesto > 0 && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${pct >= 0.8 ? "bg-emerald-100 text-emerald-700" : pct >= 0.5 ? "bg-amber-100 text-amber-700" : pct > 0 ? "bg-red-100 text-red-700" : "text-slate-300"}`}>{(pct * 100).toFixed(1)}%</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr className="bg-slate-100 font-bold border-t-2">
                    <td className="px-3 py-2.5 text-slate-700">TOTAL</td>
                    <td className="px-3 py-2.5 text-right text-slate-800">{fmtM(budgetData.reduce((s, r) => s + (r.presupuesto || 0), 0))}</td>
                    <td className="px-3 py-2.5 text-right text-slate-900">{fmtM(budgetData.reduce((s, r) => s + (r.real || 0), 0))}</td>
                    <td className="px-3 py-2.5 text-right text-slate-800">{fmtM(budgetData.reduce((s, r) => s + (r.meta_comercial || 0), 0))}</td>
                    <td className="px-3 py-2.5 text-right text-slate-900">{fmtM(budgetData.reduce((s, r) => s + (r.real_comercial || 0), 0))}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Advisors Section */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="bg-indigo-600 px-5 py-3 flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2"><UserCircle size={18} /> Asesores Comerciales — {branchName}</h3>
              {isAdmin && <button onClick={() => setShowNewAdvisor(!showNewAdvisor)} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full" data-testid="add-advisor-btn"><Plus size={12} className="inline mr-1" /> Asesor</button>}
            </div>

            {showNewAdvisor && isAdmin && (
              <div className="p-4 border-b bg-indigo-50 space-y-2" data-testid="new-advisor-form">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div><Label className="text-[10px]">Nombre *</Label><Input value={newAdvisor.name} onChange={e => setNewAdvisor({ ...newAdvisor, name: e.target.value })} className="h-8 text-xs mt-0.5" /></div>
                  <div><Label className="text-[10px]">Fecha Ingreso</Label><Input type="date" value={newAdvisor.hire_date} onChange={e => setNewAdvisor({ ...newAdvisor, hire_date: e.target.value })} className="h-8 text-xs mt-0.5" /></div>
                  <div><Label className="text-[10px]">Meta Mensual ($)</Label><Input type="number" value={newAdvisor.meta_mensual} onChange={e => setNewAdvisor({ ...newAdvisor, meta_mensual: e.target.value })} className="h-8 text-xs mt-0.5" /></div>
                  <div><Label className="text-[10px]">% Participacion</Label><Input type="number" value={newAdvisor.participacion_pct} onChange={e => setNewAdvisor({ ...newAdvisor, participacion_pct: e.target.value })} className="h-8 text-xs mt-0.5" /></div>
                </div>
                <div className="flex gap-2"><Button size="sm" className="h-8" onClick={handleCreateAdvisor}>Crear</Button><Button size="sm" variant="outline" className="h-8" onClick={() => setShowNewAdvisor(false)}>Cancelar</Button></div>
              </div>
            )}

            <div className="divide-y" data-testid="advisors-list">
              {advisors.length === 0 ? <p className="text-center text-sm text-slate-400 py-8">Sin asesores. Clic "Asesor" para agregar.</p> :
                advisors.map(adv => {
                  const isExpanded = expandedAdvisor === adv.id;
                  const metas = advisorMetas[adv.id] || {};
                  const kpis = advisorKpis[adv.id] || {};
                  return (
                    <div key={adv.id} data-testid={`advisor-${adv.id}`}>
                      {/* Advisor Header */}
                      <div className="flex items-center gap-4 px-5 py-3 cursor-pointer hover:bg-slate-50" onClick={() => handleToggleAdvisor(adv.id)}>
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                          <span className="text-indigo-700 font-bold text-sm">{adv.name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 text-sm">{adv.name}</p>
                          <div className="flex items-center gap-3 text-[10px] text-slate-400 mt-0.5">
                            {adv.hire_date && <span><CalendarBlank size={10} className="inline mr-0.5" /> Ingreso: {adv.hire_date}</span>}
                          </div>
                        </div>
                        <div className="text-right shrink-0 mr-4">
                          <p className="text-sm font-bold text-teal-700">{fmtM(adv.meta_mensual)}</p>
                          <p className="text-[10px] text-slate-400">Meta mensual | {adv.participacion_pct}% partic.</p>
                        </div>
                        {isAdmin && <button onClick={e => { e.stopPropagation(); handleDeleteAdvisor(adv.id); }} className="text-red-400 hover:text-red-600 shrink-0"><Trash size={14} /></button>}
                        {isExpanded ? <CaretUp size={16} className="text-slate-400" /> : <CaretDown size={16} className="text-slate-400" />}
                      </div>

                      {/* Expanded Detail */}
                      {isExpanded && (
                        <div className="px-5 pb-5 bg-slate-50/50 space-y-4">
                          {/* Product Metas */}
                          <div>
                            <p className="text-xs font-semibold text-slate-600 mb-2">Meta por Producto</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                              {productFields.map(f => (
                                <div key={f.id} className="bg-white border rounded-lg p-2.5 text-center">
                                  <p className="text-[9px] text-slate-500 uppercase font-medium mb-1">{f.name}</p>
                                  <EditableCell cellKey={`pm-${adv.id}-${f.id}`} value={metas[f.id] || 0}
                                    onSave={() => saveAdvisorMeta(adv.id, f.id)}
                                    fmt={v => f.type === "percentage" ? `${v}%` : f.type === "money" ? fmtM(v) : String(v)} />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* KPI Monthly Table */}
                          <div>
                            <p className="text-xs font-semibold text-slate-600 mb-2">KPIs Mensuales</p>
                            <div className="bg-white border rounded-lg overflow-hidden">
                              <table className="w-full text-xs">
                                <thead><tr className="bg-slate-50 border-b">
                                  <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500">MES</th>
                                  {kpiColumns.map(c => <th key={c.id} className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500">{c.name}</th>)}
                                </tr></thead>
                                <tbody className="divide-y">
                                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                    <tr key={m} className="hover:bg-slate-50">
                                      <td className="px-2 py-1.5 font-medium text-slate-600">{m}</td>
                                      {kpiColumns.map(c => {
                                        const val = kpis[`${m}_${c.id}`] || 0;
                                        return (
                                          <td key={c.id} className="px-2 py-1.5 text-center">
                                            <EditableCell cellKey={`kpi-${adv.id}-${m}-${c.id}`} value={val}
                                              onSave={() => saveAdvisorKpi(adv.id, m, c.id)}
                                              fmt={v => c.type === "money" ? fmtM(v) : c.type === "percentage" ? `${v}%` : String(v)} />
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
